import { z } from "zod";

// TES-1059 (TES-1054 point D, Emelie 2026-10-01) — a completion request as a
// round, with a stored status per requested item.
//
// Emelie: "Varje begärd komplettering har egen status. Patienten och Kirill ser samlad progress, exempelvis: Kompletteringar: 1 av 2 klara"
//
// A normal partial item is saved with its source and time and updates the
// progress, but creates no new review item while other normal items remain.
// When every item is done (or waived) the round is `ready`, and the doctor's
// queue gets ONE "Kompletteringar klara" item for it. An acute or red signal
// is not held by a round: it reaches the medical alerts lanes at once.

// What the round was asked on. `transfer_review` returns the review to the
// doctor's queue by itself (TES-953), so its round is recorded but gets no
// queue item of its own.
export const COMPLETION_ROUND_PARENT_KINDS = [
  "meeting",
  "transfer_review",
  "cycle",
  "lab",
  "measurement",
  // TES-1169 — "Begär komplettering" from the patient overview, when it
  // opened a case of its own (one that joined an open case is in that
  // case's round).
  "request",
] as const;
export const completionRoundParentKindSchema = z.enum(
  COMPLETION_ROUND_PARENT_KINDS,
);
export type CompletionRoundParentKind = z.infer<
  typeof completionRoundParentKindSchema
>;

// open → ready (every item done or waived) → reviewed (the doctor handled
// it). `superseded`: the patient's checklist moved on to a newer request
// before this one was complete, so it can no longer be completed.
// A ready round goes back to open if an item it counted stops counting (a
// reading the doctor then marked "Behöver ny mätning").
export const COMPLETION_ROUND_STATUSES = [
  "open",
  "ready",
  "reviewed",
  "superseded",
] as const;
export const completionRoundStatusSchema = z.enum(COMPLETION_ROUND_STATUSES);
export type CompletionRoundStatus = z.infer<typeof completionRoundStatusSchema>;

export const COMPLETION_ROUND_ITEM_STATUSES = [
  "requested",
  "done",
  "waived",
] as const;
export const completionRoundItemStatusSchema = z.enum(
  COMPLETION_ROUND_ITEM_STATUSES,
);
export type CompletionRoundItemStatus = z.infer<
  typeof completionRoundItemStatusSchema
>;

// Where the fact that completed an item came from.
export const COMPLETION_ITEM_SOURCES = [
  "patient_upload",
  "measurement",
  "self_report",
  "lab_result",
  "payment",
  "form_submission",
  "doctor",
] as const;
export const completionItemSourceSchema = z.enum(COMPLETION_ITEM_SOURCES);
export type CompletionItemSource = z.infer<typeof completionItemSourceSchema>;

export const COMPLETION_ITEM_SOURCE_LABEL: Record<
  CompletionItemSource,
  string
> = {
  patient_upload: "Uppladdat av patienten",
  measurement: "Mätning registrerad av patienten",
  self_report: "Markerat av patienten",
  lab_result: "Provsvar registrerat",
  payment: "Betalning verifierad",
  form_submission: "Formulär inskickat",
  doctor: "Läkare",
};

// The F-07 sections a transfer komplettering reopened, as one item.
export const TRANSFER_F07_ITEM_KEY = "transfer_f07";
// A doctor's "Annan specificerad komplettering" row: `custom:<taskId>`.
export const CUSTOM_ITEM_KEY_PREFIX = "custom:";

export const completionRoundItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  customTaskId: z.string().nullable(),
  // Ready to print: the task's label, or the doctor's own heading.
  label: z.string(),
  status: completionRoundItemStatusSchema,
  doneAt: z.string().nullable(),
  source: completionItemSourceSchema.nullable(),
});
export type CompletionRoundItem = z.infer<typeof completionRoundItemSchema>;

export const completionRoundSchema = z.object({
  id: z.string(),
  parentKind: completionRoundParentKindSchema,
  parentId: z.string(),
  requestedAt: z.string(),
  status: completionRoundStatusSchema,
  readyAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  items: z.array(completionRoundItemSchema),
});
export type CompletionRound = z.infer<typeof completionRoundSchema>;

// "Kompletteringar: n av m klara" over a round's items, counted the way the
// checklist counts (completionProgress): a new blood test behind an unpaid
// payment is hidden from her, so it is not counted for the doctor either.
// Waived counts as done.
export function completionRoundProgress(
  items: readonly Pick<CompletionRoundItem, "key" | "status">[],
): { done: number; total: number } {
  const paymentOpen = items.some(
    (i) => i.key === "lab_payment" && i.status === "requested",
  );
  const visible = items.filter(
    (i) => !(paymentOpen && i.key === "new_blood_test"),
  );
  return {
    done: visible.filter((i) => i.status !== "requested").length,
    total: visible.length,
  };
}

/**
 * TES-1262 (TES-1061 parity for the transfer) — the completion round a
 * decision on the transfer review is journaled in: the newest round asked on
 * that review that is still with the patient or ready for the doctor. Its
 * Kompletteringsanteckning then carries the decision, and the review's own
 * note stays the earlier, locked history. Null on a first decision. The API
 * and the admin's Webdoc step both pick the note with this, so the text
 * copied to Webdoc is the text stored.
 */
export function transferDecisionRound<
  R extends Pick<
    CompletionRound,
    "parentKind" | "parentId" | "status" | "requestedAt"
  >,
>(rounds: readonly R[], reviewId: string): R | null {
  return (
    rounds
      .filter(
        (r) =>
          r.parentKind === "transfer_review" &&
          r.parentId === reviewId &&
          (r.status === "open" || r.status === "ready"),
      )
      .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt))[0] ?? null
  );
}

// The queue title per parent, after "Kompletteringar klara".
export const COMPLETION_ROUND_PARENT_LABEL: Record<
  CompletionRoundParentKind,
  string
> = {
  meeting: "efter vårdmöte",
  transfer_review: "receptövertag",
  cycle: "månadsuppföljning",
  lab: "provsvar",
  measurement: "ny mätning av blodtryck och vilopuls",
  request: "begärd från patientöversikten",
};
